import { tokens } from '../../theme/tokens';

const { surface, line, ink, accent, signal, radius, shadow } = tokens;

/**
 * Design tokens for the application navigation shell.
 *
 * Originally transcribed from the navigation design handoff. The names are
 * kept for the shell components; the values now come from `theme/tokens.ts`
 * so the shell, the dashboards and the rest of the app share one palette.
 * Only the impersonation and pin colours are the shell's own.
 */
export const shellTokens = {
  size: {
    sidebar: 264,
    rail: 68,
    header: 56,
    banner: 44,
    /** Mobile drawer always shows labels and leaves backdrop to tap out. */
    mobileDrawer: 'min(85vw, 320px)',
  },
  color: {
    ink: ink.primary,
    body: ink.body,
    bodyAlt: ink.bodyAlt,
    muted: ink.muted,
    faint: ink.faint,
    line: line.default,
    lineSubtle: line.hairline,
    surface: surface.sunken,
    accent: accent.main,
    accentDeep: accent.hover,
    accentTint: accent.tint,
    accentBadge: accent.tintStrong,
    danger: signal.problem.ink,
    dangerDeep: signal.problem.ink,
    warning: signal.caution.ink,
    warningDeep: signal.caution.ink,
    warningTint: signal.caution.tint,
    dangerTint: signal.problem.tint,
    infoTint: signal.info.tint,
    success: signal.positive.ink,
    successTint: signal.positive.tint,
    // The impersonation banner and the pin are the shell's own signals with
    // no palette equivalent; they are the one sanctioned exception here.
    /* eslint-disable no-restricted-syntax */
    impersonation: '#7c2d12',
    impersonationIcon: '#fdba74',
    impersonationAccent: '#fed7aa',
    pin: '#f59e0b',
    pinEmpty: ink.disabled,
    disabled: ink.disabled,
    white: ink.inverse,
    /** Rail glyph mark. */
    brandGlyph: '#64ee85',
    /* eslint-enable no-restricted-syntax */
  },
  shadow: {
    card: shadow.card,
    menu: shadow.menu,
    flyout: shadow.flyout,
    profileMenu: shadow.menu,
    notifications: '0 22px 54px rgba(15,23,42,.20)',
    palette: shadow.overlay,
    tab: '0 1px 2px rgba(15,23,42,.10)',
  },
  radius: {
    keyHint: '5px',
    menuRow: `${radius.small}px`,
    navRow: `${radius.small}px`,
    control: `${radius.control}px`,
    menu: `${radius.card}px`,
    panel: `${radius.panel}px`,
    pill: `${radius.pill}px`,
  },
  motion: {
    sidebar: 'width .18s ease',
    chevron: 'transform .15s ease',
    /** How long the version string flashes "Copied". */
    copyFlashMs: 1600,
  },
  /**
   * Local stacking order. The banner sits above the header so it can never be
   * overlapped while it is making a claim about who you are.
   */
  z: {
    header: 20,
    banner: 25,
    flyout: 1200,
    menu: 1300,
    palette: 1400,
  },
} as const;

/** Icon sizing used across nav rows; stroke weight comes from the MUI outlined set. */
export const navIconSize = 17;
